import Service, { type Registry as Services, service } from '@ember/service';
import type {
  IconColor,
  IconName,
} from '@mylighthouse/prism-ember/types/components/prism/icon';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  PMS_INTEGRATION_STATUS,
} from 'frontend-utils/utils/constants/benchmark-insight';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export const INTEGRATION_STATUS = {
  INTEGRATION_REQUESTED: 'integrationRequested',
  INTEGRATION_IN_PROGRESS: 'integrationInProgress',
  MAPPING_NEEDED_INFO: 'mappingNeededInfo',
  MAPPING_NEEDED_WARNING: 'mappingNeededWarning',
  SETUP_COMPLETE_COMPSET: 'setupCompleteCompset',
  SETUP_COMPLETE_DESIRED_COMPSET: 'setupCompleteDesiredCompset',
} as const;
export type IntegrationStatus =
  (typeof INTEGRATION_STATUS)[keyof typeof INTEGRATION_STATUS];

export const INTEGRATION_STATUS_STYLES: Record<
  IntegrationStatus,
  {
    description: string;
    icon: IconName;
    iconColor?: IconColor;
    title: string;
  } & (
    | { action?: never; actionLabel?: never }
    | { action: (router: Services['router']) => void; actionLabel: string }
  )
> = {
  [INTEGRATION_STATUS.INTEGRATION_REQUESTED]: {
    icon: 'rates-lowest',
    title: tKey(
      'benchmark_insight.integration_status.integration_requested.label',
    ),
    description: tKey(
      'benchmark_insight.integration_status.integration_requested.tooltip',
    ),
  },
  [INTEGRATION_STATUS.INTEGRATION_IN_PROGRESS]: {
    icon: 'compare',
    title: tKey(
      'benchmark_insight.integration_status.integration_in_progress.label',
    ),
    description: tKey(
      'benchmark_insight.integration_status.integration_in_progress.tooltip',
    ),
  },
  [INTEGRATION_STATUS.MAPPING_NEEDED_INFO]: {
    icon: 'connection',
    title: tKey(
      'benchmark_insight.integration_status.mapping_needed_info.label',
    ),
    description: tKey(
      'benchmark_insight.integration_status.mapping_needed_info.tooltip',
    ),
    actionLabel: tKey('revenue_insight.breakdown_settings.action.map_data'),
    action: (router: Services['router']): void => {
      router.transitionTo('hotel.settings.mapping.benchmark');
    },
  },
  [INTEGRATION_STATUS.MAPPING_NEEDED_WARNING]: {
    icon: 'warning',
    iconColor: 'warning',
    title: tKey(
      'benchmark_insight.integration_status.mapping_needed_warning.label',
    ),
    description: tKey(
      'benchmark_insight.integration_status.mapping_needed.tooltip',
    ),
    actionLabel: tKey('revenue_insight.breakdown_settings.action.map_data'),
    action: (router: Services['router']): void => {
      router.transitionTo('hotel.settings.mapping.benchmark');
    },
  },
  [INTEGRATION_STATUS.SETUP_COMPLETE_COMPSET]: {
    icon: 'circle-checkmark',
    iconColor: 'success',
    title: tKey('benchmark_insight.integration_status.setup_completed.label'),
    description: tKey(
      'benchmark_insight.integration_status.setup_completed.tooltip.compset',
    ),
    actionLabel: tKey(
      'benchmark_insight.integration_status.setup_completed.action.compset',
    ),
    action: (router: Services['router']): void => {
      router.transitionTo('hotel.settings.competitors.benchmark.compset');
    },
  },
  [INTEGRATION_STATUS.SETUP_COMPLETE_DESIRED_COMPSET]: {
    icon: 'circle-checkmark',
    iconColor: 'success',
    title: tKey('benchmark_insight.integration_status.setup_completed.label'),
    description: tKey(
      'benchmark_insight.integration_status.setup_completed.tooltip.desired_compset',
    ),
    actionLabel: tKey('benchmark_insight.desired_compset.builder.header'),
    action: (router: Services['router']): void => {
      router.transitionTo('hotel.benchmark-insight.desired-compset');
    },
  },
};

export default class BenchmarkInsightIntegrationStatusService extends Service {
  @service declare access: Services['access'];
  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightDesiredCompsetService: Services['benchmark-insight/competitors/desired-compset'];
  @service('benchmark-insight/beni-hotels')
  declare beniHotelsService: Services['benchmark-insight/beni-hotels'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get integrationStatus() {
    const {
      benchmarkInsightDataUnmappedSince,
      benchmarkInsightPmsIntegrationStatus,
    } = this.hotelAndMarketService.hotel;

    if (
      benchmarkInsightPmsIntegrationStatus ===
      PMS_INTEGRATION_STATUS.NO_INTEGRATION
    ) {
      return INTEGRATION_STATUS.INTEGRATION_REQUESTED;
    }

    if (
      benchmarkInsightPmsIntegrationStatus ===
      PMS_INTEGRATION_STATUS.INTEGRATION_IN_PROGRESS
    ) {
      return INTEGRATION_STATUS.INTEGRATION_IN_PROGRESS;
    }

    if (
      benchmarkInsightPmsIntegrationStatus ===
      PMS_INTEGRATION_STATUS.MAPPING_NEEDED
    ) {
      const dataUnmappedSinceDayjs = dayjs(benchmarkInsightDataUnmappedSince);
      const daysSinceDataUnmapped = dayjs().diff(dataUnmappedSinceDayjs, 'day');

      if (daysSinceDataUnmapped > 30) {
        return INTEGRATION_STATUS.MAPPING_NEEDED_WARNING;
      }

      return INTEGRATION_STATUS.MAPPING_NEEDED_INFO;
    }

    // Only show "Set up compset" card once we've loaded the hotel's available
    // comparison sets. While loading, availableComparisonSets is [] which would
    // incorrectly trigger the setup card.
    if (
      benchmarkInsightPmsIntegrationStatus === PMS_INTEGRATION_STATUS.DONE &&
      this.access.hasBenchmarkInsightCompset &&
      !this.beniHotelsService.isLoading &&
      !this.beniHotelsService.availableComparisonSets.includes(
        BENI_COMPARISON_SETS.COMPSET,
      )
    ) {
      return INTEGRATION_STATUS.SETUP_COMPLETE_COMPSET;
    }

    if (
      benchmarkInsightPmsIntegrationStatus === PMS_INTEGRATION_STATUS.DONE &&
      !this.access.hasBenchmarkInsightCompset &&
      !this.benchmarkInsightDesiredCompsetService.desiredCompsetTask
        .isRunning &&
      !this.benchmarkInsightDesiredCompsetService.desiredCompetitors.length
    ) {
      return INTEGRATION_STATUS.SETUP_COMPLETE_DESIRED_COMPSET;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showIntegrationStatus() {
    return (
      this.router.currentRouteName !== 'hotel.benchmark-insight.upsell' &&
      this.router.currentRouteName !==
        'hotel.benchmark-insight.terms-and-conditions'
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/integration-status': BenchmarkInsightIntegrationStatusService;
  }
}
